import {
  Component,
  inject,
  input,
  Input,
  OnInit,
  OnDestroy,
  computed,
  effect,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { debounceTime, Subject, switchMap, takeUntil, of, catchError, map, Observable } from 'rxjs';

import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AddressesService } from '@core/services/addresses/addresses.service';
import { LocationsService } from '@core/services/locations/locations.service';
import { ErrorMessageComponent } from '@shared/components/forms/error-message/error-message.component';

import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { MessageService } from 'primeng/api';
import { FloatLabel } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { Skeleton } from 'primeng/skeleton';
import { Tooltip } from 'primeng/tooltip';
import { Location } from '@features/purchase-flow/domain/models/location.model';
import { AuthStore } from '@features/authentication/state/auth.store';
import { ClientStore } from '@core/state/client/client.store';
import { AddAddressModalComponent } from '@features/user/components/modals/add-address-modal/add-address-modal.component';
import { LoggerService } from '@core/services/logger/logger.service';
import {
  CountryInfo,
  CityResult,
  LocationV3Result,
  CountriesResponse,
} from '@core/models/geonames.model';

type SmartSuggestionItem = LocationV3Result & { label: string; _type: string };

@Component({
  selector: 'd2d-input-address',
  standalone: true,
  imports: [
    TranslocoPipe,
    AutoCompleteModule,
    FormsModule,
    SelectModule,
    ReactiveFormsModule,
    ErrorMessageComponent,
    FloatLabel,
    Skeleton,
    Tooltip,
    NgOptimizedImage,
    AddAddressModalComponent,
  ],
  templateUrl: './input-address.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './input-address.component.scss',
})
export class InputAddressComponent implements OnInit, OnDestroy {
  private readonly authStore = inject(AuthStore);
  private readonly clientStore = inject(ClientStore);
  private readonly messageService = inject(MessageService);
  private readonly addressesService = inject(AddressesService);
  private readonly locationsService = inject(LocationsService);
  private readonly translocoService = inject(TranslocoService);
  private readonly logger = inject(LoggerService).forContext('InputAddress');

  account = computed(() => this.authStore.currentAccount());
  isLogged = computed(() => this.account() !== null);

  get clientTitle(): string {
    const config = this.clientStore.clientConfig();
    return config?.main_title?.trim() || config?.client_name?.trim() || '';
  }

  get savedAddressesLabel(): string {
    if (this.syntheticAddressHasClientTitle) {
      return '';
    }
    return this.locationForm.disabled ? 'Dirección asignada' : 'Mis direcciones guardadas';
  }

  @Input() label = '';
  @Input({ required: true }) id = 'inputAddressId';
  @Input({ required: true }) locationForm!: FormControl<Location | null>;
  labelColor = input('');
  showDropdown = input<boolean>(true);
  /**
   * Código ISO de país al que debe quedar bloqueado el selector de país,
   * independientemente de si el resto de la dirección es editable.
   * `null`/vacío = sin restricción (comportamiento previo).
   */
  fixedCountryCode = input<string | null>(null);

  get isCountryFixed(): boolean {
    return !!this.fixedCountryCode();
  }

  /**
   * Cuando un mismo formulario de búsqueda muestra dos de estos componentes
   * (origen y destino), solo el primario ofrece el aviso para registrar una
   * dirección: evita duplicar el mismo bloque dos veces en pantalla.
   */
  isPrimaryAddressField = input<boolean>(true);
  /**
   * Consulta por nombre de referencia de template (no por la clase) para que
   * `AddAddressModalComponent` solo quede referenciada como tipo: así el
   * compilador puede seguir extrayéndola a un chunk aparte en el @defer de
   * abajo, en vez de forzarla al bundle inicial de home.
   */
  addAddressModal = viewChild<AddAddressModalComponent>('addAddressModal');

  // Saved addresses
  userAddresses: Location[] = [];
  selectedSavedAddress: Location | null = null;
  loadingUserAddresses = false;
  private syntheticAddress: Location | null = null;
  private syntheticAddressHasClientTitle = false;

  get savedAddressOptions(): Location[] {
    this.syntheticAddressHasClientTitle = false;
    const fixedCode = this.fixedCountryCode();
    const userAddresses = fixedCode
      ? this.userAddresses.filter(
          (address) => (address.country_code || address.country) === fixedCode,
        )
      : this.userAddresses;
    if (this.locationForm.disabled && this.locationForm.value) {
      const currentLocation = this.locationForm.value;
      const clientTitle = this.clientTitle;
      const exists = userAddresses.some(
        (a) =>
          a.postal_code === currentLocation.postal_code &&
          a.city === currentLocation.city &&
          a.country_code === currentLocation.country_code,
      );
      if (exists) {
        if (clientTitle) {
          const matching = userAddresses.find(
            (a) =>
              a.postal_code === currentLocation.postal_code &&
              a.city === currentLocation.city &&
              a.country_code === currentLocation.country_code,
          );
          if (matching) {
            matching.label = clientTitle;
          }
        }
        return userAddresses;
      }
      if (this.syntheticAddress && clientTitle) {
        this.syntheticAddress.label = clientTitle;
        this.syntheticAddressHasClientTitle = true;
      }
      return this.syntheticAddress ? [this.syntheticAddress, ...userAddresses] : userAddresses;
    }
    return userAddresses;
  }

  // Unified form
  locationSearchForm = new FormGroup({
    country: new FormControl<string>('', [Validators.required]),
    city: new FormControl<CityResult | string | null>(null),
    query: new FormControl<string>('', [Validators.required, Validators.maxLength(100)]),
  });

  // Suggestions
  countriesSuggestions: CountryInfo[] = [];
  smartSuggestions: { label: string; items: SmartSuggestionItem[] }[] = [];

  // Loading states
  loadingCountries = false;
  loadingSmartSearch = false;
  hasLoadingError = false;

  // Subjects for debouncing
  private smartSearch$ = new Subject<{
    query: string;
    country: string;
  }>();
  private destroy$ = new Subject<void>();

  // Flag to prevent clearing when populating form programmatically
  private isPopulatingForm = false;

  // Display text for current location
  currentLocationText = '';

  /**
   * Una vez el usuario interactúa (foco) con el país o el campo de dirección,
   * el texto de ejemplo ("Ej: 08001 o Passeig de Gracia 100") deja de mostrarse.
   */
  hasFocusedAddressBlock = false;

  onAddressBlockFocus(): void {
    this.hasFocusedAddressBlock = true;
  }

  /**
   * El modal se carga en un bloque @defer (para no engordar el bundle inicial
   * de home). Si el usuario hace clic antes de que termine de cargar, queda
   * pendiente y se abre solo apenas el componente esté disponible (ver el
   * `effect` en el constructor).
   */
  private openAddAddressModalPending = false;

  openAddAddressModal(): void {
    const modal = this.addAddressModal();
    if (modal) {
      modal.open();
    } else {
      this.openAddAddressModalPending = true;
    }
  }

  onAddressAdded(): void {
    this.getUserAddresses();
  }

  constructor() {
    effect(() => {
      if (this.isLogged()) {
        this.getUserAddresses();
      } else {
        this.userAddresses = [];
        this.selectedSavedAddress = null;
      }
    });

    effect(() => {
      const modal = this.addAddressModal();
      if (modal && this.openAddAddressModalPending) {
        this.openAddAddressModalPending = false;
        modal.open();
      }
    });

    // Si se guarda/edita una dirección desde CUALQUIER instancia de este
    // componente (p. ej. origen y destino en el mismo formulario), todas las
    // demás deben enterarse y refrescar su propia copia de "mis direcciones"
    // sin esperar a un recargo de página.
    this.addressesService.addressAdded$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.isLogged()) {
        this.getUserAddresses();
      }
    });

    // Re-aplicar el país fijo si cambia (p. ej. cuando la config del cliente
    // termina de cargar después de que este componente ya se inicializó).
    effect(() => {
      this.fixedCountryCode();
      if (this.locationForm) {
        if (this.locationForm.value) {
          this.populateFormFromLocation(this.locationForm.value);
        }
        this.syncDisabledState();
      }
    });

    // Re-ejecutar si cambia la configuración del cliente (p. ej. cuando termina de cargar su título)
    effect(() => {
      this.clientStore.clientConfig();
      if (this.locationForm?.disabled && this.locationForm?.value) {
        this.ensureCurrentLocationSelected();
      }
    });
  }

  ngOnInit(): void {
    this.initSearchListeners();
    this.setupFormStateManagement();
    this.syncDisabledState();

    // Watch for locationForm disabled state changes (e.g. when client address is set)
    this.locationForm.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncDisabledState());

    this.loadCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Popular con valor inicial si existe
        if (this.locationForm.value) {
          this.populateFormFromLocation(this.locationForm.value);
          this.updateCurrentLocationText(this.locationForm.value);
        }
        this.syncDisabledState();
      });

    // Watch for external changes to locationForm
    this.locationForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.updateCurrentLocationText(value);
      if (value) {
        this.populateFormFromLocation(value);
        this.syncDisabledState();
      } else {
        // Si el valor es null, limpiar el formulario interno
        this.locationSearchForm.reset({
          country: '',
          city: null,
          query: '',
        });
        // Deshabilitar city y query ya que no hay país seleccionado
        this.locationSearchForm.get('city')?.disable();
        this.locationSearchForm.get('query')?.disable();
        // Limpiar la dirección guardada seleccionada
        this.selectedSavedAddress = null;
        // Si el país está fijo por regla de negocio, no debe quedar en blanco/editable
        this.applyFixedCountry();
        this.syncDisabledState();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Sync disabled state from external locationForm to internal locationSearchForm.
   * When the client address is locked (e.g. hotel as destination), country and
   * postal code fields must appear disabled.
   */
  private syncDisabledState(): void {
    if (this.locationForm.disabled) {
      this.locationSearchForm.disable({ emitEvent: false });
      this.ensureCurrentLocationSelected();
    } else {
      this.locationSearchForm.get('country')?.enable({ emitEvent: false });
      const countryValue = this.locationSearchForm.get('country')?.value;
      if (countryValue && !this.loadingCountries) {
        this.locationSearchForm.get('city')?.enable({ emitEvent: false });
        this.locationSearchForm.get('query')?.enable({ emitEvent: false });
      }
    }
    this.applyFixedCountry();
  }

  /**
   * Fuerza el control de país al valor resuelto por la regla de negocio de
   * país fijo, sin importar el estado (habilitado/deshabilitado) del resto
   * de la dirección. No modifica la ubicación para permitir su intercambio.
   */
  private applyFixedCountry(): void {
    const fixedCode = this.fixedCountryCode();
    if (!fixedCode) return;

    const countryControl = this.locationSearchForm.get('country');
    if (countryControl && countryControl.value !== fixedCode) {
      countryControl.setValue(fixedCode, { emitEvent: false });
    }
    countryControl?.disable({ emitEvent: false });
  }

  /**
   * When form is disabled and a location exists, ensure selectedSavedAddress
   * reflects the current location so both component instances keep the same structure.
   */
  private ensureCurrentLocationSelected(): void {
    const currentLocation = this.locationForm.value;
    if (!currentLocation) return;

    const clientTitle = this.clientTitle;

    if (this.userAddresses.length > 0) {
      const matchingAddress = this.userAddresses.find(
        (addr) =>
          addr.postal_code === currentLocation.postal_code &&
          addr.city === currentLocation.city &&
          addr.country_code === currentLocation.country_code,
      );
      if (matchingAddress) {
        if (clientTitle) {
          matchingAddress.label = clientTitle;
        }
        this.selectedSavedAddress = matchingAddress;
        return;
      }
    }

    this.syntheticAddress = {
      ...currentLocation,
      label:
        clientTitle || currentLocation.label || this.buildAssignedAddressLabel(currentLocation),
    };
    this.selectedSavedAddress = this.syntheticAddress;
  }

  /**
   * Builds the summary label for the assigned address.
   * If a client title exists, uses the client name/title instead of the address.
   * Otherwise falls back to `[Nombre del Negocio] - [Calle], [Ciudad]`.
   */
  private buildAssignedAddressLabel(location: Location): string {
    const clientTitle = this.clientTitle;
    if (clientTitle) {
      return clientTitle;
    }

    const streetAndCity = [location.address_line1, location.city]
      .filter((part) => part && part.trim() !== '')
      .join(', ');

    if (location.business_name) {
      return streetAndCity
        ? `${location.business_name} - ${streetAndCity}`
        : location.business_name;
    }

    return streetAndCity || location.city || '';
  }

  // Setup form state management
  private setupFormStateManagement() {
    // Disable city and query initially
    this.locationSearchForm.get('city')?.disable();
    this.locationSearchForm.get('query')?.disable();

    // Watch country changes
    this.locationSearchForm
      .get('country')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((country: unknown) => {
        if (country && !this.loadingCountries && !this.locationForm.disabled) {
          // Habilitar city y query cuando hay un país seleccionado
          this.locationSearchForm.get('city')?.enable({ emitEvent: false });
          this.locationSearchForm.get('query')?.enable({ emitEvent: false });

          // Solo limpiar si el usuario está cambiando manualmente el país
          // (no cuando se está poblando programáticamente)
          if (!this.isPopulatingForm) {
            // Limpiar city y query cuando cambia el país
            this.locationSearchForm.patchValue(
              {
                city: null,
                query: '',
              },
              { emitEvent: false },
            );

            // Limpiar la dirección guardada seleccionada y la ubicación actual
            this.selectedSavedAddress = null;
            this.currentLocationText = '';

            // Reflejar el país elegido en el FormControl externo (igual que
            // una dirección precargada con solo el país) en vez de dejarlo en
            // null. Si se deja en null, esta selección solo vive en el
            // formulario interno de este componente y una operación como el
            // swap de direcciones -que solo mueve valores del FormControl
            // externo- la pierde en vez de trasladarla al otro lado.
            const partialLocation = this.buildPartialLocation();
            if (partialLocation) {
              this.locationForm.setValue(partialLocation, { emitEvent: false });
              this.locationForm.markAsDirty();
            }
          }
        } else {
          // Deshabilitar city y query cuando no hay país
          this.locationSearchForm.get('city')?.disable();
          this.locationSearchForm.get('query')?.disable();
          if (!this.isPopulatingForm) {
            this.selectedSavedAddress = null;
            this.currentLocationText = '';
            if (this.locationForm.value !== null) {
              this.locationForm.setValue(null, { emitEvent: false });
              this.locationForm.markAsDirty();
            }
          }
        }
      });

    // Watch query changes (typing, clearing, deleting text)
    this.locationSearchForm
      .get('query')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((query: unknown) => {
        if (!this.isPopulatingForm && !this.locationForm.disabled) {
          // Si el usuario escribe o borra en query manualmente, la dirección anterior ya no es válida
          this.selectedSavedAddress = null;
          this.currentLocationText = '';

          // Igual que con el país: reflejar lo escrito (aunque no se haya
          // seleccionado ninguna sugerencia) en el FormControl externo en vez
          // de dejarlo en null, para que sobreviva a un swap de direcciones.
          const partialLocation = this.buildPartialLocation((query as string) || '');
          if (partialLocation) {
            this.locationForm.setValue(partialLocation, { emitEvent: false });
            this.locationForm.markAsDirty();
          } else if (this.locationForm.value !== null) {
            this.locationForm.setValue(null, { emitEvent: false });
            this.locationForm.markAsDirty();
          }
        }
      });
  }

  /**
   * Construye una Location parcial a partir del país actualmente
   * seleccionado (y, opcionalmente, el texto de código postal/dirección
   * escrito pero aún no confirmado con una sugerencia). Devuelve null si
   * todavía no hay país, único caso en el que no hay nada que reflejar en el
   * FormControl externo.
   */
  private buildPartialLocation(query = ''): Location | null {
    const countryCode = this.locationSearchForm.get('country')?.value as string;
    if (!countryCode) return null;

    const countryInfo = this.countriesSuggestions.find((c) => c.code === countryCode);
    return {
      country: countryInfo?.name || countryCode,
      country_code: countryCode,
      city: '',
      state: '',
      postal_code: '',
      address_line1: query,
    };
  }

  // Load countries on init
  private loadCountries(): Observable<CountryInfo[]> {
    this.loadingCountries = true;
    this.locationSearchForm.get('country')?.disable({ emitEvent: false });

    return this.locationsService.getCountriesV2('es').pipe(
      map((response: CountriesResponse) => {
        this.countriesSuggestions = response.countries;
        this.loadingCountries = false;
        if (!this.locationForm.disabled) {
          this.locationSearchForm.get('country')?.enable({ emitEvent: false });
        }
        return response.countries;
      }),
      catchError((error: Error) => {
        this.loadingCountries = false;
        if (!this.locationForm.disabled) {
          this.locationSearchForm.get('country')?.enable({ emitEvent: false });
        }
        this.handleError(error, 'Error al cargar países');
        this.logger.error('Error loading countries:', error);
        return of([]);
      }),
    );
  }

  // Load user saved addresses
  private getUserAddresses() {
    this.loadingUserAddresses = true;
    this.addressesService.getMyAddresses().subscribe({
      next: (addresses) => {
        this.loadingUserAddresses = false;

        if (addresses.length === 0) return;

        this.userAddresses = addresses.map((addr) => ({
          id: addr.id,
          label: addr.label,
          address_line1: addr.address_line1,
          address_line2: addr.address_line2,
          city: addr.city,
          state: addr.state || '',
          country: addr.country,
          country_code: addr.country_code,
          postal_code: addr.postal_code,
          address_type: addr.address_type,
        }));

        if (this.locationForm.disabled) {
          this.ensureCurrentLocationSelected();
        }
      },
      error: (error) => {
        this.loadingUserAddresses = false;
        this.handleError(
          error,
          this.translocoService.translate('inputAddress.loadSavedAddressesErrorSummary'),
        );
        this.logger.error('Error loading user addresses:', error);
      },
    });
  }

  // Initialize search listeners with debouncing
  private initSearchListeners() {
    // Smart search listener
    this.smartSearch$
      .pipe(
        debounceTime(500),
        switchMap(({ query, country }) => {
          this.loadingSmartSearch = true;

          return this.locationsService.searchV3(country, query).pipe(
            map((response) => this.processV3Results(response.results)),
            catchError(() => of([])),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (suggestions) => {
          this.loadingSmartSearch = false;
          this.smartSuggestions = suggestions;
        },
        error: (error) => {
          this.loadingSmartSearch = false;
          this.handleError(
            error,
            this.translocoService.translate('inputAddress.searchLocationErrorSummary'),
          );
          this.logger.error('Error in smart search:', error);
        },
      });
  }

  // Process V3 results into grouped suggestions
  private processV3Results(
    results: LocationV3Result[],
  ): { label: string; items: SmartSuggestionItem[] }[] {
    if (!results || results.length === 0) return [];

    // Map all results to a common structure
    const items = results.map((item) => ({
      ...item,
      label: item.postal_code ? `${item.postal_code} - ${item.city}` : item.city,
      _type: 'v3',
    }));

    return [
      {
        label: 'Resultados',
        items: items,
      },
    ];
  }

  // Smart search method
  smartSearch(event: AutoCompleteCompleteEvent) {
    const query = event.query?.trim();
    if (!query || query.length < 2) {
      this.smartSuggestions = [];
      return;
    }

    const country = this.locationSearchForm.get('country')?.value;
    if (!country) {
      this.smartSuggestions = [];
      return;
    }

    this.smartSearch$.next({ query, country });
  }

  // Handler when location is selected (unified)
  onLocationSelected(event: { value?: SmartSuggestionItem } | SmartSuggestionItem) {
    const item = (event as { value?: SmartSuggestionItem }).value ?? (event as SmartSuggestionItem);

    if (item._type === 'v3') {
      this.mapV3ResultToLocation(item);
    }
  }

  private mapV3ResultToLocation(result: LocationV3Result) {
    this.isPopulatingForm = true;

    // Update form with postal code or query
    const queryValue = result.postal_code || result.city;
    this.locationSearchForm.patchValue(
      {
        query: queryValue,
        city: result.city,
      },
      { emitEvent: false },
    );

    // Get country name
    const country = this.locationSearchForm.get('country')?.value;
    const countryInfo = this.countriesSuggestions.find((c) => c.code === country);

    const location: Location = {
      country: countryInfo?.name || country || '',
      country_code: country || '',
      state: result.state || '',
      city: result.city,
      postal_code: result.postal_code,
      coordinates: {
        latitude: result.latitude,
        longitude: result.longitude,
      },
    };

    this.updateCurrentLocationText(location);
    this.locationForm.patchValue(location);
    this.locationForm.markAsDirty();

    this.isPopulatingForm = false;
  }

  // Handler when saved address is selected
  onSavedAddressSelected(address: Location | null) {
    if (!address) {
      this.selectedSavedAddress = null;

      // Limpiar el formulario interno
      this.locationSearchForm.reset({
        country: '',
        city: null,
        query: '',
      });

      // Deshabilitar city y query ya que no hay país seleccionado
      this.locationSearchForm.get('city')?.disable();
      this.locationSearchForm.get('query')?.disable();

      // Limpiar el formulario principal
      this.locationForm.setValue(null);
      this.locationForm.markAsDirty();

      // Limpiar el localStorage
      const storageKey =
        this.id === 'from' ? 'selected_origin_address_id' : 'selected_destination_address_id';
      localStorage.removeItem(storageKey);

      return;
    }

    // Establecer bandera para evitar limpieza automática
    this.isPopulatingForm = true;

    // Auto-fill the unified form
    // Guardar como string ya que viene de una dirección guardada
    this.locationSearchForm.patchValue({
      country: address.country_code || '',
      city: address.city,
      query: address.postal_code || address.address_line1 || '',
    });

    // Emit to main form
    this.locationForm.patchValue(address);
    this.locationForm.markAsDirty();

    // Store address ID if available
    if ('id' in address && 'address_type' in address && address.id && address.address_type) {
      const storageKey =
        this.id === 'from' ? 'selected_origin_address_id' : 'selected_destination_address_id';

      localStorage.setItem(storageKey, String(address.id));
    }

    // Resetear bandera después de poblar
    this.isPopulatingForm = false;
  }

  // Populate form from external location value
  private populateFormFromLocation(location: Location) {
    // Establecer bandera para evitar limpieza automática
    this.isPopulatingForm = true;

    // Handle cases where country might be a code or country_code might exist
    // If country_code exists, use it. Otherwise, check if country looks like a code (2-3 chars uppercase)
    let countryCode = location.country_code || '';
    if (!countryCode && location.country) {
      const isCountryCode = /^[A-Z]{2,3}$/.test(location.country);
      countryCode = isCountryCode ? location.country : '';
    }

    // Guardar como string ya que viene de una ubicación externa
    this.locationSearchForm.patchValue(
      {
        country: countryCode,
        city: location.city,
        query: location.postal_code || location.address_line1 || '',
      },
      { emitEvent: false },
    );

    // Enable city and query fields if country is valid (unless locationForm is disabled)
    if (countryCode && !this.loadingCountries && !this.locationForm.disabled) {
      this.locationSearchForm.get('city')?.enable({ emitEvent: false });
      this.locationSearchForm.get('query')?.enable({ emitEvent: false });
    }

    // Cuando el formulario está bloqueado, mostrar la ubicación actual en el select
    if (this.locationForm.disabled) {
      this.ensureCurrentLocationSelected();
    } else if (this.userAddresses.length > 0) {
      // Intentar encontrar y seleccionar la dirección guardada correspondiente
      // Esto es útil cuando se hace switch entre campos
      const matchingAddress = this.userAddresses.find(
        (addr) =>
          addr.postal_code === location.postal_code &&
          addr.city === location.city &&
          addr.country_code === location.country_code,
      );

      if (matchingAddress) {
        this.selectedSavedAddress = matchingAddress;
      } else {
        // Si no hay coincidencia, limpiar la selección
        this.selectedSavedAddress = null;
      }
    }

    // Resetear bandera después de poblar
    this.isPopulatingForm = false;
  }

  // Get icon for result group
  getGroupIcon(label: string): string {
    if (label === 'Resultados') {
      return 'pi pi-map-marker';
    }
    return 'pi pi-search';
  }

  // Update display text for current location
  private updateCurrentLocationText(location: Location | null) {
    if (!location) {
      this.currentLocationText = '';
      return;
    }

    const parts = [
      location.postal_code,
      location.city,
      location.state,
      location.country_code /* || location.country */,
    ].filter((p) => p && p.trim() !== '');

    this.currentLocationText = parts.join(', ');
  }

  get googleMapsUrl(): string {
    const location = this.locationForm.value;
    if (!location) return '';

    const query = [location.postal_code, location.city, location.state, location.country]
      .filter(Boolean)
      .join(', ');

    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  }

  // Error handler
  private handleError(error: unknown, summary: string) {
    this.hasLoadingError = true;

    // Reset error state after 5 seconds
    setTimeout(() => {
      this.hasLoadingError = false;
    }, 5000);

    // Check if it's a GeoNames timeout or rate limit error
    const errorMessage =
      error instanceof Error
        ? error.message
        : typeof error === 'object' && error !== null && 'message' in error
          ? String((error as { message: unknown }).message)
          : '';
    let detail = this.translocoService.translate('inputAddress.defaultErrorDetail');

    if (errorMessage.includes('GeoNames') || errorMessage.includes('timeout')) {
      detail = this.translocoService.translate('inputAddress.geoNamesUnavailableDetail');
    } else if (errorMessage.includes('rate limit')) {
      detail = this.translocoService.translate('inputAddress.rateLimitDetail');
    }

    this.messageService.add({
      severity: 'error',
      summary,
      detail,
      life: 5000,
    });
  }
}
